import { type CardPendingState } from '@/components/molecules/CardPending/CardPending.tsx'
import { Toaster } from '@/components/molecules/Toaster/Toaster.tsx'
import {
  CardListAllocated,
  type CardListAllocatedActions,
  type CardListAllocatedData,
  type CardListAllocatedLabels,
  type CardListAllocatedProps,
} from '@/components/organisms/CardListAllocated/CardListAllocated.tsx'
import {
  CardListAssigned,
  type CardListAssignedActions,
  type CardListAssignedData,
  type CardListAssignedLabels,
  type CardListAssignedOptions,
  type CardListAssignedProps,
} from '@/components/organisms/CardListAssigned/CardListAssigned.tsx'
import {
  CardListCompleted,
  type CardListCompletedActions,
  type CardListCompletedData,
  type CardListCompletedLabels,
  type CardListCompletedProps,
} from '@/components/organisms/CardListCompleted/CardListCompleted.tsx'
import {
  CardListOnboarded,
  type CardListOnboardedActions,
  type CardListOnboardedData,
  type CardListOnboardedLabels,
  type CardListOnboardedProps,
} from '@/components/organisms/CardListOnboarded/CardListOnboarded.tsx'
import {
  CardListPending,
  type CardListPendingActions,
  type CardListPendingData,
  type CardListPendingLabels,
  type CardListPendingProps,
} from '@/components/organisms/CardListPending/CardListPending.tsx'
import {
  Map,
  type MapActions,
  type MapConfiguration,
  type MapData,
  type MapOptions,
  type MapState,
} from '@/components/organisms/Map/Map.tsx'
import {
  MainLayout,
  type MainLayoutAction,
  type MainLayoutData,
  type MainLayoutLabels,
  type MainLayoutNavigation,
  type MainLayoutState,
  type MainLayoutTimeFormat,
} from '@/components/templates/MainLayout/MainLayout.tsx'
import { cn } from '@/lib/utils.ts'
import { type ToasterActions } from '@/model/contexts/useDashboardAlertToasts'

export type AppViewTimeFormat = MainLayoutTimeFormat

export type AppViewNavigation = MainLayoutNavigation & MapOptions & CardListAssignedOptions

export interface AppViewLabels extends MainLayoutLabels {
  requestList: {
    pending: CardListPendingLabels
    assigned: CardListAssignedLabels
    allocated: CardListAllocatedLabels
    onboarded: CardListOnboardedLabels
    completed: CardListCompletedLabels
  }
}

export interface AppViewData extends MainLayoutData {
  requestList: {
    pending?: CardListPendingData
    assigned?: CardListAssignedData
    allocated?: CardListAllocatedData
    onboarded?: CardListOnboardedData
    completed?: CardListCompletedData
  }
  map: MapData
}

export interface AppViewAction
  extends
    MainLayoutAction,
    Omit<CardListPendingActions, 'onCancelRequest'>,
    CardListAssignedActions,
    CardListAllocatedActions,
    CardListOnboardedActions,
    CardListCompletedActions,
    ToasterActions,
    MapActions {
  onCancelPendingRequest: CardListPendingActions['onCancelRequest']
}

export interface AppViewState
  extends
    Omit<MainLayoutState, 'disabled' | 'selectedTeamId'>,
    Pick<CardPendingState, 'selectedTeam'>,
    Omit<MapState, 'activeView' | 'selectedTeamIds' | 'selectedTransportRequestIds'> {
  pending?: CardListPendingProps['state']
  assigned?: CardListAssignedProps['state']
  allocated?: CardListAllocatedProps['state']
  onboarded?: CardListOnboardedProps['state']
  completed?: CardListCompletedProps['state']
}

export interface AppViewProps extends AppViewAction, AppViewTimeFormat, MapConfiguration {
  labels: AppViewLabels
  data: AppViewData
  state: AppViewState
  navigation: AppViewNavigation
}

export const AppView = ({
  labels,
  data,
  state,
  navigation: { mapPendingTeamStatusFilterItems = [], teamStatusOptions, teamAvailableInRegionOptions, ...navigation },
  googleMapConfiguration,
  timeAgoFormatter,
  willExecuteInTimeAgoFormatter,
  onActivate,
  onAllocateManually,
  onArrivedAtDestination,
  onAssignTeam,
  onAssignManually,
  onBackToPiloteClicked,
  onBaseChanged,
  onCancelPendingRequest,
  onCancelRequest,
  onMobileRequestToggled,
  onChangeDonation,
  onClose,
  onCustomerNotFound,
  onCustomerTakenCareOf,
  onTeamSortByChanged,
  onNavigationChanged,
  onStartingLocationChanged,
  onSearchChanged,
  onNavigationSortChanged,
  onTeamSelected,
  onTelevisionToggled,
  onSuperPower,
  onViewChanged,
  onWaitTimeToggled,
  onChangeAssignationState,
  onNotificationSoundToggled,
  onPutOnHold,
  onReactivate,
  onMenuFilterChanged,
  onMapLoad,
  onMapUnmount,
  onHighlight,
  onTeamMessageClick,
  onChannelMessageClick,
}: AppViewProps) => {
  const displayMap = state.view === 'map'
  const disabled =
    state.pending?.selected !== undefined && data.requestList.pending?.some((r) => r.id === state.pending?.selected)
  const selectedTransportRequestId = state[state.activeStatus]?.active

  return (
    <>
      <MainLayout
        labels={labels}
        data={data}
        state={{ ...state, selectedTeamId: state.selectedTeam?.id, disabled }}
        navigation={navigation}
        onBackToPiloteClicked={onBackToPiloteClicked}
        onBaseChanged={onBaseChanged}
        onMobileRequestToggled={onMobileRequestToggled}
        onTeamSortByChanged={onTeamSortByChanged}
        onNavigationChanged={onNavigationChanged}
        onStartingLocationChanged={onStartingLocationChanged}
        onSearchChanged={onSearchChanged}
        onNavigationSortChanged={onNavigationSortChanged}
        onTeamSelected={onTeamSelected}
        onTelevisionToggled={onTelevisionToggled}
        onSuperPower={onSuperPower}
        onViewChanged={onViewChanged}
        onWaitTimeToggled={onWaitTimeToggled}
        onChangeAssignationState={onChangeAssignationState}
        onNotificationSoundToggled={onNotificationSoundToggled}
        timeAgoFormatter={timeAgoFormatter}
        willExecuteInTimeAgoFormatter={willExecuteInTimeAgoFormatter}>
        <Map
          className={cn(
            displayMap || state.television ? 'block' : 'hidden',
            !state.television ? '-mt-[83px]' /* 167px/2 */ : '-mt-[24px]' /* 49px/2 */
          )}
          style={{ width: !state.television ? 'calc(100vw - 328px)' : '100vw' }}
          activeView={state.television ? 'all' : state.activeStatus}
          selectedTeamIds={state.selectedTeam?.id ? [state.selectedTeam.id] : []}
          selectedTransportRequestIds={selectedTransportRequestId ? [selectedTransportRequestId] : []}
          mapPendingTeamStatusFilterItems={mapPendingTeamStatusFilterItems}
          mapPendingTeamStatusActiveFilters={state.mapPendingTeamStatusActiveFilters}
          data={data.map}
          onMenuFilterChanged={onMenuFilterChanged}
          onMapLoad={onMapLoad}
          onMapUnmount={onMapUnmount}
          googleMapConfiguration={googleMapConfiguration}
        />
        <div
          className={cn(
            state.television ? 'hidden' : 'fixed top-[169px] right-[144px] overflow-y-auto bg-white',
            displayMap && 'min-w-[184px]'
          )}
          style={{
            height: 'calc(100vh - 180px)',
            width: displayMap ? '' : 'calc(100% - 144px)',
          }}>
          {state.activeStatus === 'pending' ? (
            <CardListPending
              labels={labels.requestList.pending}
              data={data.requestList.pending}
              state={state.pending}
              size={displayMap ? 'sm' : 'default'}
              selectedTeam={state.selectedTeam}
              timeAgoFormatter={timeAgoFormatter}
              willExecuteInTimeAgoFormatter={willExecuteInTimeAgoFormatter}
              onActivate={onActivate}
              onAssignTeam={onAssignTeam}
              onAssignManually={onAssignManually}
              onCancelRequest={onCancelPendingRequest}
              onClose={onClose}
            />
          ) : state.activeStatus === 'assigned' ? (
            <CardListAssigned
              labels={labels.requestList.assigned}
              options={{ teamStatusOptions, teamAvailableInRegionOptions }}
              data={data.requestList.assigned}
              state={state.assigned}
              size={displayMap ? 'sm' : 'default'}
              timeAgoFormatter={timeAgoFormatter}
              onActivate={onActivate}
              onAllocateManually={onAllocateManually}
              onCancelRequest={onCancelRequest}
              onPutOnHold={onPutOnHold}
            />
          ) : state.activeStatus === 'allocated' ? (
            <CardListAllocated
              labels={labels.requestList.allocated}
              options={{ teamStatusOptions, teamAvailableInRegionOptions }}
              data={data.requestList.allocated}
              state={state.allocated}
              size={displayMap ? 'sm' : 'default'}
              timeAgoFormatter={timeAgoFormatter}
              onActivate={onActivate}
              onCancelRequest={onCancelRequest}
              onCustomerNotFound={onCustomerNotFound}
              onCustomerTakenCareOf={onCustomerTakenCareOf}
              onPutOnHold={onPutOnHold}
            />
          ) : state.activeStatus === 'onboarded' ? (
            <CardListOnboarded
              labels={labels.requestList.onboarded}
              options={{ teamStatusOptions, teamAvailableInRegionOptions }}
              data={data.requestList.onboarded}
              state={state.onboarded}
              size={displayMap ? 'sm' : 'default'}
              timeAgoFormatter={timeAgoFormatter}
              onActivate={onActivate}
              onArrivedAtDestination={onArrivedAtDestination}
              onCancelRequest={onCancelRequest}
            />
          ) : state.activeStatus === 'completed' ? (
            <CardListCompleted
              labels={labels.requestList.completed}
              data={data.requestList.completed}
              state={state.completed}
              size={displayMap ? 'sm' : 'default'}
              onActivate={onActivate}
              onChangeDonation={onChangeDonation}
              onReactivate={onReactivate}
            />
          ) : null}
        </div>
      </MainLayout>
      <Toaster
        onHighlight={onHighlight}
        onTeamMessageClick={onTeamMessageClick}
        onChannelMessageClick={onChannelMessageClick}
      />
    </>
  )
}
